/**
 * SVG rendering of the editor document.
 *
 * Pure string building: the controller injects the result with `innerHTML`
 * into a live `<svg>`, which is what makes the geometry interactions 60 fps
 * without React reconciling thousands of nodes (spec 2026-08-22-02 §2).
 *
 * The world group maps millimetres straight to screen pixels
 * (`screen = mm·k + offset`), so every stroke width below is a real-world
 * millimetre value and stays correct at any zoom.
 */
import { bboxOfPoints, centroid, distanceToRing, fnum, formatAreaM2, type Point } from './geometry'
import { mmPerUnit, mmToUnits, unitsPerMm, type LengthContext } from './calibrate'
import { wallJunctionPolygons, type JunctionWall } from './joins'
import { DEFAULT_WALL_THICKNESS, openingFrame, openingSymbol } from './openings'
import { PLAN_COLORS, grouperTints } from './palette'
import { documentPoints, isGrouperElement, ownerName, ownerNameIsDerived, pointInSpace, spaceArea, spaceFallbackName, spaceHoles, type EditorElement, type GrouperRef, type PlanSymbol, type SceneDoc, type SpaceElement } from './scene'

/**
 * Paint order. `runs` (building networks) sits above the openings and below
 * the equipment, as in the server render: a fan-coil sits on its duct.
 */
export const LAYER_IDS = ['underlays', 'spaces', 'walls', 'openings', 'runs', 'equipment', 'labels'] as const
export type LayerId = (typeof LAYER_IDS)[number]

export interface LayerState {
    on: boolean
    opacity: number
}

export type LayerStates = Record<LayerId, LayerState>

export const defaultLayerStates = (): LayerStates => ({
    underlays: { on: true, opacity: 0.85 },
    spaces: { on: true, opacity: 0.45 },
    walls: { on: true, opacity: 1 },
    openings: { on: true, opacity: 1 },
    runs: { on: true, opacity: 1 },
    equipment: { on: true, opacity: 1 },
    labels: { on: true, opacity: 1 },
})

/**
 * Label metrics, in screen pixels — the only unit a reader perceives.
 * `LABEL_CHAR_PX_PER_LINE_PX` is a rough average glyph advance for the sans
 * stack, enough to tell "this name is three times wider than the room".
 */
const LABEL_LINE_PX = 13
const LABEL_MIN_WIDTH_PX = 44
const LABEL_CHAR_PX_PER_LINE_PX = 0.5

/**
 * Whether a label is worth drawing on the shape it names.
 *
 * Labels are sized in screen pixels (`13 / scale` world units), so they do not
 * shrink with the plan: zoomed far out, a room a few pixels across still gets
 * a two-line 13 px readout, and the labels layer turns into a pile of
 * overlapping text with no drawing left under it. This gates on the element's
 * own on-screen footprint rather than on an absolute zoom level — a calibrated
 * floor in millimetres and an uncalibrated underlay whose frame is the unit
 * square sit orders of magnitude apart in `scale`, and only what they cover on
 * screen is comparable between them. Names come back as soon as their shape is
 * big enough to hold them.
 *
 * The text's own length counts, against the shape's *larger* dimension: a long
 * name needs a big shape under it, but a narrow corridor is allowed to carry
 * one — it has the room along its length, which is where the eye follows it.
 */
export function labelFitsOnScreen(
    pts: Point[],
    scale: number,
    text: string,
    lines: number = 1,
    linePx: number = LABEL_LINE_PX,
): boolean {
    const bb = bboxOfPoints(pts)
    if (!bb) return false
    const w = (bb.x1 - bb.x0) * scale
    const h = (bb.y1 - bb.y0) * scale
    return w >= LABEL_MIN_WIDTH_PX
        && h >= linePx * 1.15 * lines
        && Math.max(w, h) >= text.length * linePx * LABEL_CHAR_PX_PER_LINE_PX
}

export function esc(s: unknown): string {
    return String(s ?? '').replace(/[&<>"']/g, (c) =>
        ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string)
}

export interface RenderOptions {
    layers: LayerStates
    /** Screen pixels per millimetre — hairlines and hit areas stay zoom-invariant. */
    scale: number
    /** Builds the authenticated render URL of a raster representation. */
    underlayHref: (repUid: string) => string
    /** The currently selected element, highlighted in the world layers. */
    selectedKey?: string | null
    /**
     * Every selected key — the primary plus its shift-selected companions
     * (`selectedKeys()` in `tools.ts`). A companion reads on the plan too, in
     * the primary's stroke colour at a lighter weight, so a group edit's reach
     * is visible without cross-checking the layer panel. Handles, the vertex
     * being dragged, the ambiguity spot and the parent-space blink stay tied
     * to `selectedKey` alone — only the paint below reads this.
     */
    selectedKeys?: string[]
    /**
     * The space the selection parents to, pulsed so that "this radiator belongs
     * to that room" is visible on the drawing rather than only in the
     * inspector's parent field.
     */
    parentSpaceKey?: string | null
    /**
     * Draw the grouping overlay (spec 2026-08-30-01 §8): each space filed under
     * a unit / common area gets that grouper's tint, and a grouper with no
     * outline of its own gets its name at its members' centroid. Purely
     * derived — no zone polygon is ever stored.
     */
    showGroupers?: boolean
    /** The element under the pointer, lit so a click is predictable (§8). */
    hoverKey?: string | null
    /**
     * Overlapping grouper outlines under the pointer, painted red because one
     * click cannot mean all of them (§7). Repeated clicks walk the list.
     */
    ambiguousKeys?: string[]
    /**
     * Localizes an asset type code, so a room nobody named can be drawn as
     * "Chambre 2" (spec 2026-08-31-02 §2). Optional: the renderer is a pure
     * function of the document, and without it an unnamed room falls back to
     * its local id exactly as before.
     */
    typeLabelOf?: (code: string) => string
    /**
     * Network codes whose runs are hidden — the layer panel's per-network and
     * per-family switches. The `runs` layer toggle hides them all at once.
     */
    hiddenNetworks?: ReadonlySet<string>
    /** Localizes a network code from the catalogue, for the run's tooltip. */
    networkLabelOf?: (code: string) => string
}

/** Screen weights of a run, in pixels: the white casing and the coloured line. */
const RUN_CASING_PX = 5
const RUN_LINE_PX = 2.5

/**
 * How one network run is drawn. Never like a wall: a coloured polyline with
 * round caps and joins over a white casing, its network's dash when it has
 * one, and — when the run knows its real width — a translucent band at that
 * width underneath, so a 600 mm duct reads as one at every zoom. Colour and
 * dash come from the ontology's catalogue; a code it does not know draws in
 * the neutral run colour.
 */
export function runMarkup(
    el: { points: Point[]; network: string; widthMm?: number },
    network: { color?: string; dash?: string } | undefined,
    ks: number,
    upm: number,
    state: { selected?: boolean; hovered?: boolean } = {},
): string {
    const pts = el.points.map((p) => `${fnum(p.x)},${fnum(p.y)}`).join(' ')
    const color = network?.color ?? PLAN_COLORS.runFallback
    const dash = (network?.dash ?? '')
        .split(/[\s,]+/).map(Number).filter((n) => Number.isFinite(n) && n > 0)
        .map((n) => fnum(n / ks)).join(' ')
    const line = (RUN_LINE_PX * (state.selected || state.hovered ? 1.6 : 1)) / ks
    return (
        (el.widthMm && el.widthMm > 0
            ? `<polyline class="plan-run-band" points="${pts}" fill="none" stroke="${esc(color)}" stroke-opacity="0.22"` +
              ` stroke-width="${fnum(el.widthMm * upm)}" stroke-linecap="round" stroke-linejoin="round"/>`
            : '') +
        (state.selected
            ? `<polyline class="plan-run-selection" points="${pts}" fill="none" stroke="var(--primary)" stroke-opacity="0.35"` +
              ` stroke-width="${fnum((RUN_CASING_PX * 2.2) / ks)}" stroke-linecap="round" stroke-linejoin="round"/>`
            : '') +
        `<polyline class="plan-run-casing" points="${pts}" fill="none" stroke="${PLAN_COLORS.runCasing}"` +
        ` stroke-width="${fnum(RUN_CASING_PX / ks)}" stroke-linecap="round" stroke-linejoin="round"/>` +
        `<polyline class="plan-run-line" points="${pts}" fill="none" stroke="${esc(color)}"` +
        ` stroke-width="${fnum(line)}" stroke-linecap="round" stroke-linejoin="round"` +
        (dash ? ` stroke-dasharray="${dash}"` : '') + '/>'
    )
}

/** A riser of unknown section, or on an uncalibrated frame, in screen pixels. */
export const RISER_RADIUS_PX = 7
/** How far off a riser's disc a run end of its network may lie and still join it. */
export const RISER_JUNCTION_MM = 100

/**
 * A riser's disc radius in plan units: its real section when the frame is
 * calibrated and the diameter known, never smaller than a few pixels; else a
 * fixed screen size.
 */
export function riserRadius(el: { diameterMm?: number }, ks: number, calibrated: boolean, upm: number): number {
    const fixed = RISER_RADIUS_PX / ks
    if (!calibrated || !el.diameterMm || el.diameterMm <= 0) return fixed
    return Math.max((el.diameterMm * upm) / 2, 4 / ks)
}

/**
 * How one riser is drawn (spec 2026-10-19-03 §1): a disc in its network's
 * colour with the passage glyph in white — a centre dot when it crosses the
 * floor, an up arrow where the column starts and rises, a down arrow where it
 * ends — and a junction dot on every run end of the same network that comes
 * off it. Same markup in the editor, the viewer and the patterns page.
 */
export function riserMarkup(
    el: { position: Point; passage?: string },
    network: { color?: string } | undefined,
    r: number,
    ks: number,
    junctions: Point[] = [],
    state: { selected?: boolean; hovered?: boolean } = {},
): string {
    const { x, y } = el.position
    const color = esc(network?.color ?? PLAN_COLORS.runFallback)
    const h = r * 0.6
    const glyph = el.passage === 'origin'
        ? `<path class="plan-riser-glyph" data-passage="origin" d="M ${fnum(x)} ${fnum(y - h)} L ${fnum(x + h)} ${fnum(y + h * 0.7)} L ${fnum(x - h)} ${fnum(y + h * 0.7)} Z" fill="#ffffff"/>`
        : el.passage === 'terminal'
            ? `<path class="plan-riser-glyph" data-passage="terminal" d="M ${fnum(x)} ${fnum(y + h)} L ${fnum(x + h)} ${fnum(y - h * 0.7)} L ${fnum(x - h)} ${fnum(y - h * 0.7)} Z" fill="#ffffff"/>`
            : `<circle class="plan-riser-glyph" data-passage="through" cx="${fnum(x)}" cy="${fnum(y)}" r="${fnum(r * 0.3)}" fill="#ffffff"/>`
    return (
        (state.selected
            ? `<circle class="plan-riser-selection" cx="${fnum(x)}" cy="${fnum(y)}" r="${fnum(r + RUN_CASING_PX / ks)}" fill="var(--primary)" fill-opacity="0.35"/>`
            : '') +
        `<circle class="plan-riser-disc" cx="${fnum(x)}" cy="${fnum(y)}" r="${fnum(r)}" fill="${color}"` +
        ` stroke="${PLAN_COLORS.runCasing}" stroke-width="${fnum((state.hovered ? 2.5 : 1.5) / ks)}"/>` +
        glyph +
        junctions.map((j) =>
            `<circle class="plan-riser-junction" cx="${fnum(j.x)}" cy="${fnum(j.y)}" r="${fnum(Math.max(r * 0.35, 3 / ks))}"` +
            ` fill="${color}" stroke="${PLAN_COLORS.runCasing}" stroke-width="${fnum(1 / ks)}"/>`).join('')
    )
}

/** The run ends of `network` lying within the junction tolerance of a riser's disc. */
export function riserJunctions(
    riser: { position: Point; network: string },
    runs: ReadonlyArray<{ network: string; points: Point[] }>,
    r: number,
    upm: number,
): Point[] {
    const out: Point[] = []
    const reach = r + RISER_JUNCTION_MM * upm
    for (const run of runs) {
        if (run.network !== riser.network || run.points.length < 2) continue
        for (const end of [run.points[0], run.points[run.points.length - 1]]) {
            if (Math.hypot(end.x - riser.position.x, end.y - riser.position.y) <= reach) out.push(end)
        }
    }
    return out
}

/**
 * What a space is called on the plan: its **asset's** name first.
 *
 * A representation carries a label of its own, and an imported floorplan is
 * full of ones that no longer match — a space labelled `T5-3171` depicting the
 * asset `E17-T5-2`. The inspector edits the asset (spec 2026-08-30-03 §12), so
 * the drawing has to read the same way or the two contradict each other on the
 * same screen. The element's own label is the fallback, which is what a bundle
 * of anonymous shapes still has.
 */
const spaceLabel = (
    el: EditorElement & { kind: 'space' },
    typeLabelOf?: (code: string) => string,
): string => {
    const own = ownerName(el.owner)
    if (own) return own
    // A room nobody named — pending, auto-named on the server, or just handed
    // back by clearing the field — reads as its type and its number, "Chambre
    // 2" (spec 2026-08-31-02 §2), from the type and number as currently
    // edited. That derivation outranks the element's own label, because the
    // label is where the deleted name survives: a representation keeps the
    // string it was saved with, and printing it would contradict the inspector
    // and the asset page, which both show what the server derived.
    const derived = typeLabelOf ? spaceFallbackName(el.owner, typeLabelOf) : ''
    if (derived && ownerNameIsDerived(el.owner)) return derived
    // Nobody owns the name and nothing can derive one: the element's own label
    // is a real identity someone chose — an imported plan's `T5-3171` — and is
    // all a bundle of anonymous shapes has. The stored name is the last word.
    return el.name || derived || el.owner.assetName || el.source.localId
}

/** The identity a grouper is tracked by: its uid, or its pending key. */
export const grouperIdOf = (g: GrouperRef | undefined): string | undefined =>
    g?.assetUid ?? g?.assetKey

/**
 * Inherited surroundings read a step back from the subject (spec 2026-09-17-01
 * §4). A lot opened from its floor's frame shows the floor's own geometry
 * around it, and without this the reader cannot tell the lot they asked for
 * from the page it happens to sit on.
 *
 * The treatment is a *named state*, like `.plan-hover` and `.plan-ambiguous`:
 * the renderer only says which elements are context, and `index.css` owns how
 * far back they sit. Deliberately not applied to underlays — the inherited scan
 * is the background base layer the view exists to show.
 */
const CONTEXT_CLASS = 'plan-context'

/**
 * Where a space's label goes: its centroid — unless it has holes. A plateau's
 * centroid tends to land on the very core it goes around, under the label of
 * the room drawn there, so a holed space takes the point of its floor farthest
 * from every edge, holes included, on a coarse grid: a cheap pole of
 * inaccessibility, paid only by the spaces that need it.
 */
export function spaceLabelPoint(el: SpaceElement): Point {
    const c = centroid(el.boundary)
    const holes = spaceHoles(el)
    const bb = holes.length ? bboxOfPoints(el.boundary) : null
    if (!bb) return c
    const clearance = (p: Point): number => (pointInSpace(p, el)
        ? Math.min(distanceToRing(p, el.boundary), ...holes.map((h) => distanceToRing(p, h)))
        : -1)
    const steps = 16
    let best = c
    let bestClearance = clearance(c)
    for (let i = 1; i < steps; i++) {
        for (let j = 1; j < steps; j++) {
            const p = { x: bb.x0 + ((bb.x1 - bb.x0) * i) / steps, y: bb.y0 + ((bb.y1 - bb.y0) * j) / steps }
            const d = clearance(p)
            if (d > bestClearance) {
                best = p
                bestClearance = d
            }
        }
    }
    return best
}

/** A ring as one SVG sub-path. */
export const ringPath = (ring: Point[]): string =>
    `M${ring.map((p) => `${fnum(p.x)},${fnum(p.y)}`).join(' L')} Z`

/**
 * A space's outline. With holes it is one path, a sub-path per ring, under the
 * even-odd rule: the pillar stays unfilled, is not a pointer target — a click
 * there reaches what is drawn inside it — and the whole outline, holes
 * included, is still the one element the space's asset uid points at (spec
 * 2026-10-19-07). Without, it stays the polygon every selector already reads.
 */
export function spaceShape(el: SpaceElement, pts: string, before: string, after: string): string {
    const holes = spaceHoles(el).filter((h) => h.length >= 3)
    if (!holes.length) return `<polygon${before} points="${pts}"${after}/>`
    return `<path${before} fill-rule="evenodd" d="${[el.boundary, ...holes].map(ringPath).join(' ')}"${after}/>`
}

const attrs = (el: EditorElement, selection?: 'primary' | 'companion'): string =>
    ` data-kind="${el.kind}" data-key="${esc(el.key)}"` +
    (el.owner.assetUid ? ` data-asset-uid="${esc(el.owner.assetUid)}"` : '') +
    (el.source.context ? ` class="${CONTEXT_CLASS}" data-context="true"` : '') +
    (selection ? ` data-selected="${selection}"` : '')

/** Renders every world layer, keyed by layer id. */
export function renderWorld(doc: SceneDoc, opts: RenderOptions): Record<LayerId, string> {
    const ks = Math.max(opts.scale, 1e-9)
    // Room labels are a real-world readout: they multiply through the frame's
    // calibration, or print raw plan units when there is none (spec
    // 2026-08-25-04 §3).
    const scaleMm = mmPerUnit(doc.calibration)
    // A wall stored without a thickness is drawn with the same screen-relative
    // default a new wall gets on an uncalibrated frame.
    const lengthCtx: LengthContext = {
        k: ks,
        extent: () => {
            const bb = bboxOfPoints(documentPoints(doc))
            return bb ? Math.max(bb.x1 - bb.x0, bb.y1 - bb.y0) : 0
        },
    }
    const hairline = 1.5 / ks
    const hitW = 14 / ks
    const out: Record<LayerId, string> = {
        underlays: '', spaces: '', walls: '', openings: '', runs: '', equipment: '', labels: '',
    }
    /** Collected as the walls are drawn; their junctions are filled after. */
    const joinable: JunctionWall[] = []
    /** Risers, appended to the runs layer once every run is drawn. */
    let riserMarkupOut = ''

    // The grouping overlay is read off the document in one pass: which
    // groupers have an outline of their own (that outline is then the shape
    // shown), and where the members of the ones that do not sit.
    const grouperOutlines = new Set<string>()
    const grouperMembers = new Map<string, { name: string; pts: Point[]; extent: Point[] }>()
    const grouperIds = new Set<string>()
    if (opts.showGroupers) {
        for (const el of doc.elements) {
            if (el.kind !== 'space') continue
            if (isGrouperElement(el)) {
                const id = el.owner.assetUid ?? el.owner.newAsset?.key
                if (id) {
                    grouperOutlines.add(id)
                    grouperIds.add(id)
                }
                continue
            }
            const id = grouperIdOf(el.owner.grouper)
            if (!id || el.boundary.length < 3) continue
            grouperIds.add(id)
            const entry = grouperMembers.get(id)
                ?? { name: el.owner.grouper?.name ?? '', pts: [], extent: [] }
            entry.pts.push(centroid(el.boundary))
            // The members' own outlines, not just their centroids: a grouper
            // with a single member has no spread at all, and its name is
            // perfectly legible over that one room.
            entry.extent.push(...el.boundary)
            if (!entry.name && el.owner.grouper?.name) entry.name = el.owner.grouper.name
            grouperMembers.set(id, entry)
        }
    }
    const tints = grouperTints(grouperIds)

    if (opts.layers.underlays.on) {
        for (const u of doc.underlays) {
            if (!u.visible) continue
            const t = u.transform
            out.underlays +=
                `<g data-kind="underlay" data-key="${esc(u.key)}" transform="translate(${fnum(t.tx)},${fnum(t.ty)}) rotate(${fnum(t.rotation_deg)}) scale(${t.scale})">` +
                `<image x="0" y="0" width="1" height="${fnum(u.height)}" preserveAspectRatio="none"` +
                ` opacity="${u.opacity * opts.layers.underlays.opacity}"` +
                ` data-underlay-key="${esc(u.key)}" href="${esc(opts.underlayHref(u.repUid))}"/></g>`
        }
    }

    for (const el of doc.elements) {
        // `primary` is the one the inspector edits and the only one that keeps
        // handles (tools.ts); `companion` is everything else the shift-select
        // group holds. `selected` covers both, for the states that must yield
        // to *either* — hover and a grouper's own tint (§6 of the spec).
        const primary = opts.selectedKey === el.key
        const companion = !primary && (opts.selectedKeys?.includes(el.key) ?? false)
        const selected = primary || companion
        const selectionState = primary ? 'primary' : companion ? 'companion' : undefined
        switch (el.kind) {
            case 'space': {
                if (!opts.layers.spaces.on) break
                const pts = el.boundary.map((p) => `${fnum(p.x)},${fnum(p.y)}`).join(' ')
                if (!pts) break
                const shape = (before: string, after: string) => spaceShape(el, pts, before, after)
                // The parent of the selection is *announced*, not decorated:
                // `.plan-parent-space` blinks it twice and hands the polygon
                // straight back to its ordinary paint. Every attribute below is
                // therefore the normal one — the CSS animation carries the whole
                // effect and owns nothing once it has finished. Tied to the
                // primary alone — a companion cannot be its own group's parent.
                const isParent = !primary && opts.parentSpaceKey === el.key
                // A grouper outline is not a room and must not read as one:
                // dashed, cooler, and tinted with its own colour so the split
                // of a floor is legible under the rooms drawn on top of it.
                const grouper = isGrouperElement(el)
                const ownId = el.owner.assetUid ?? el.owner.newAsset?.key
                const memberTint = opts.showGroupers ? grouperIdOf(el.owner.grouper) : undefined
                const tint = grouper
                    ? ((ownId && tints.get(ownId)) ?? PLAN_COLORS.grouperStroke)
                    : memberTint ? tints.get(memberTint) : undefined
                const hovered = !selected && opts.hoverKey === el.key
                const ambiguous = grouper && (opts.ambiguousKeys?.includes(el.key) ?? false)
                const fill = grouper ? PLAN_COLORS.grouperFill : PLAN_COLORS.spaceFill
                const stroke = selected
                    ? 'var(--primary)'
                    : grouper ? PLAN_COLORS.grouperStroke : PLAN_COLORS.spaceStroke
                // Hover and ambiguity are *named states*, not paint mixed in
                // here: `.plan-hover` and `.plan-ambiguous` own the treatment
                // (spec 2026-08-30-03 §7, §10), so the stylesheet is where one
                // goes to change how they look. All they need from the
                // renderer is the zoom-invariant hairline to multiply, handed
                // over as a custom property — `px` inside an SVG is one user
                // unit, so the value stays in plan space at every zoom.
                const cls = [
                    isParent ? 'plan-parent-space' : '',
                    grouper ? 'plan-grouper-outline' : '',
                    hovered ? 'plan-hover' : '',
                    ambiguous ? 'plan-ambiguous' : '',
                ].filter(Boolean).join(' ')
                const label = spaceLabel(el, opts.typeLabelOf)
                out.spaces +=
                    `<g${attrs(el, selectionState)}>` +
                    // The edge is a thin thing to aim at, so an invisible halo
                    // carries the same `data-key` and widens it to a comfortable
                    // target (spec 2026-08-30-03 §5). It stays `stroke`-only for
                    // the same reason the outline does: a grouper's *interior*
                    // must never be a DOM hit target, or it would swallow the
                    // clicks meant for the rooms drawn on top of it. The
                    // interior is reachable — `pickGrouperAt` resolves it
                    // geometrically, after the DOM has had first refusal.
                    (grouper
                        ? shape(' class="plan-grouper-halo"',
                            ` fill="none" stroke="transparent" stroke-width="${fnum(hairline * 14)}" pointer-events="stroke"`)
                        : '') +
                    shape(
                        (cls ? ` class="${cls}"` : '') +
                        (hovered || ambiguous ? ` style="--plan-hairline:${fnum(hairline)}px"` : ''),
                        ` fill="${fill}"` +
                        ` fill-opacity="${opts.layers.spaces.opacity}" stroke="${grouper && !selected ? tint : stroke}"` +
                        (grouper ? ` stroke-dasharray="${fnum(hairline * 8)} ${fnum(hairline * 5)}" pointer-events="stroke"` : '') +
                        // The primary keeps the doubled hairline it always had; a
                        // companion reads at a step below it — enough to tell them
                        // apart without one looking like an afterthought.
                        ` stroke-width="${fnum(hairline * (primary ? 2 : companion ? 1.5 : grouper ? 1.8 : 1))}"`,
                    ) +
                    (!grouper && tint
                        ? shape(' class="plan-grouper-tint"', ` fill="${tint}" fill-opacity="0.3" pointer-events="none"`)
                        : '') +
                    `<title>${esc(label)}</title></g>`
                if (opts.layers.labels.on && el.boundary.length >= 3
                    && labelFitsOnScreen(el.boundary, ks, label, 2)) {
                    const c = spaceLabelPoint(el)
                    const fs = LABEL_LINE_PX / ks
                    out.labels +=
                        `<text class="plan-space-label" x="${fnum(c.x)}" y="${fnum(c.y)}" text-anchor="middle"` +
                        ` font-size="${fnum(fs)}" fill="${PLAN_COLORS.label}" pointer-events="none">` +
                        `<tspan x="${fnum(c.x)}" font-weight="600">${esc(label)}</tspan>` +
                        `<tspan x="${fnum(c.x)}" dy="${fnum(fs * 1.15)}" fill="${PLAN_COLORS.labelMuted}" font-size="${fnum(fs * 0.85)}">` +
                        `${esc(formatAreaM2(spaceArea(el), scaleMm))}</tspan></text>`
                }
                break
            }
            case 'wall': {
                if (!opts.layers.walls.on) break
                const th = el.thickness || mmToUnits(DEFAULT_WALL_THICKNESS, doc.calibration, lengthCtx)
                joinable.push({ start: el.start, end: el.end, thickness: th })
                out.walls +=
                    `<g${attrs(el, selectionState)} opacity="${opts.layers.walls.opacity}">` +
                    // A wall's stroke-width *is* its real-world thickness, so a
                    // companion cannot be told apart by a thicker line the way a
                    // space is — it takes the same colour at a lower opacity
                    // instead, which is the "lighter weight" this file's other
                    // categories express as a narrower stroke.
                    `<line x1="${fnum(el.start.x)}" y1="${fnum(el.start.y)}" x2="${fnum(el.end.x)}" y2="${fnum(el.end.y)}"` +
                    ` stroke="${selected ? 'var(--primary)' : PLAN_COLORS.wall}"` +
                    (companion ? ' stroke-opacity="0.6"' : '') +
                    ` stroke-width="${fnum(th)}" stroke-linecap="butt"/>` +
                    `<line class="plan-hit" x1="${fnum(el.start.x)}" y1="${fnum(el.start.y)}" x2="${fnum(el.end.x)}" y2="${fnum(el.end.y)}"` +
                    ` stroke-width="${fnum(Math.max(th, hitW))}"/>` +
                    `<title>${esc(el.owner.assetName || el.source.localId)}</title></g>`
                break
            }
            case 'opening': {
                if (!opts.layers.openings.on) break
                const f = openingFrame(el, unitsPerMm(doc.calibration, lengthCtx))
                const voidPts = f.voidPoints.map((p) => `${fnum(p.x)},${fnum(p.y)}`).join(' ')
                out.openings +=
                    `<g${attrs(el, selectionState)} opacity="${opts.layers.openings.opacity}">` +
                    `<polygon points="${voidPts}" fill="${PLAN_COLORS.voidFill}" stroke="none"/>` +
                    openingSymbol(el, unitsPerMm(doc.calibration, lengthCtx)) +
                    // A companion draws a bar across the opening's own axis, in
                    // the primary's colour: the symbol underneath keeps its own
                    // ironmongery, so this is additive rather than a repaint —
                    // exactly what a companion needs to read without being
                    // mistaken for the element an edit is anchored on.
                    (companion
                        ? `<line class="plan-companion" x1="${fnum(f.cx - f.ux * f.halfW)}" y1="${fnum(f.cy - f.uy * f.halfW)}"` +
                          ` x2="${fnum(f.cx + f.ux * f.halfW)}" y2="${fnum(f.cy + f.uy * f.halfW)}"` +
                          ` stroke="var(--primary)" stroke-opacity="0.65" stroke-width="${fnum(Math.max(f.thickness * 0.4, hairline * 1.5))}"` +
                          ' stroke-linecap="round" pointer-events="none"/>'
                        : '') +
                    `<line class="plan-hit" x1="${fnum(f.cx - f.ux * f.halfW)}" y1="${fnum(f.cy - f.uy * f.halfW)}"` +
                    ` x2="${fnum(f.cx + f.ux * f.halfW)}" y2="${fnum(f.cy + f.uy * f.halfW)}"` +
                    ` stroke-width="${fnum(Math.max(f.thickness + 60 / ks, hitW))}"/>` +
                    `<title>${esc(el.owner.assetName || el.openingKind)}</title></g>`
                break
            }
            case 'equipment': {
                if (!opts.layers.equipment.on) break
                const symbol = el.typeCode ? doc.symbols[el.typeCode] : undefined
                // The hit area follows the symbol, not the anchor: clicking the
                // shape has to select it, and off-centre anchors are common.
                const off = equipmentFootprintOffset(symbol, el.width, el.depth)
                const hitWidth = Math.max(el.width, hitW)
                const hitDepth = Math.max(el.depth, hitW)
                out.equipment +=
                    `<g${attrs(el, selectionState)} opacity="${opts.layers.equipment.opacity}">` +
                    (symbol ? symbolMarkup(symbol, el.position, el.rotationDeg, el.width, el.depth)
                        : fallbackEquipment(el.position, el.rotationDeg, el.width, el.depth, el.typeCode, hairline)) +
                    // A companion's footprint outline, in the primary's colour —
                    // the symbol itself keeps its own fixed fill, so a radiator
                    // and a boiler shift-selected together both read as part of
                    // the group without either losing what type it is.
                    (companion
                        ? `<polygon class="plan-companion" points="${equipmentFootprintCorners(el, symbol).map((p) => `${fnum(p.x)},${fnum(p.y)}`).join(' ')}"` +
                          ` fill="none" stroke="var(--primary)" stroke-opacity="0.65" stroke-width="${fnum(hairline * 1.5)}" pointer-events="none"/>`
                        : '') +
                    // Fill only: `.plan-hit` paints a transparent stroke, and
                    // without a width of its own that stroke is one plan unit
                    // wide. On a sheet stored as 1 x 1 unit it covers the whole
                    // drawing and swallows every click meant for a unit.
                    `<rect class="plan-hit" x="${fnum(el.position.x + off.x - hitWidth / 2)}" y="${fnum(el.position.y + off.y - hitDepth / 2)}"` +
                    ` width="${fnum(hitWidth)}" height="${fnum(hitDepth)}" style="stroke:none"` +
                    ` transform="rotate(${fnum(el.rotationDeg)} ${fnum(el.position.x)} ${fnum(el.position.y)})"/>` +
                    `<title>${esc(el.owner.assetName || el.typeCode || 'equipment')}</title></g>`
                break
            }
            case 'run': {
                if (!opts.layers.runs.on || opts.hiddenNetworks?.has(el.network)) break
                const network = doc.networks?.find((n) => n.code === el.network)
                const hovered = !selected && opts.hoverKey === el.key
                const pts = el.points.map((p) => `${fnum(p.x)},${fnum(p.y)}`).join(' ')
                const label = opts.networkLabelOf?.(el.network) ?? el.network
                out.runs +=
                    `<g${attrs(el, selectionState)} data-network="${esc(el.network)}" opacity="${opts.layers.runs.opacity}">` +
                    runMarkup(el, network, ks, unitsPerMm(doc.calibration, lengthCtx), { selected, hovered }) +
                    `<polyline class="plan-hit" points="${pts}" fill="none" stroke-width="${fnum(hitW)}"/>` +
                    `<title>${esc(el.owner.assetName ? `${el.owner.assetName} — ${label}` : label)}</title></g>`
                break
            }
            case 'riser': {
                if (!opts.layers.runs.on || opts.hiddenNetworks?.has(el.network)) break
                const network = doc.networks?.find((n) => n.code === el.network)
                const hovered = !selected && opts.hoverKey === el.key
                const upm = unitsPerMm(doc.calibration, lengthCtx)
                const r = riserRadius(el, ks, !!doc.calibration, upm)
                const runs = doc.elements.filter((x): x is Extract<EditorElement, { kind: 'run' }> =>
                    x.kind === 'run' && !opts.hiddenNetworks?.has(x.network))
                const label = opts.networkLabelOf?.(el.network) ?? el.network
                const name = el.owner.assetName || el.label
                // Drawn after every run, so the line comes off the disc rather than over it.
                riserMarkupOut +=
                    `<g${attrs(el, selectionState)} data-network="${esc(el.network)}" data-passage="${esc(el.passage ?? '')}" opacity="${opts.layers.runs.opacity}">` +
                    riserMarkup(el, network, r, ks, riserJunctions(el, runs, r, upm), { selected, hovered }) +
                    `<circle class="plan-hit" cx="${fnum(el.position.x)}" cy="${fnum(el.position.y)}" r="${fnum(Math.max(r, hitW / 2))}" style="stroke:none"/>` +
                    `<title>${esc(name ? `${name} — ${label}` : label)}</title></g>`
                break
            }
        }
    }
    out.runs += riserMarkupOut

    // Butt caps leave an empty angle wherever walls meet; these fill it. They
    // are drawn last, over the segments, and take no pointer events — a
    // junction is not an element, it is the absence of one.
    if (joinable.length > 1) {
        const fills = wallJunctionPolygons(joinable)
            .map((poly) => `<polygon points="${poly.map((p) => `${fnum(p.x)},${fnum(p.y)}`).join(' ')}"/>`)
            .join('')
        if (fills) {
            out.walls +=
                `<g class="plan-wall-joins" fill="${PLAN_COLORS.wall}" stroke="none"` +
                ` opacity="${opts.layers.walls.opacity}" pointer-events="none">${fills}</g>`
        }
    }
    // A grouper with no outline of its own is named at its members' centroid —
    // the only thing the overlay draws that is not already a shape on the plan.
    // Gated on the grouping toggle alone, not on the labels layer: it is the
    // overlay's own readout, and hiding it with the room names would leave the
    // tints unexplained.
    if (opts.showGroupers) {
        for (const [id, entry] of grouperMembers) {
            if (grouperOutlines.has(id) || !entry.name || !entry.pts.length) continue
            const fs = 15 / ks
            // Same readability floor as the room names: a tint whose members
            // cover a few pixels cannot carry its name legibly either.
            if (!labelFitsOnScreen(entry.extent, ks, entry.name, 1, 15)) continue
            const c = centroid(entry.pts.length >= 3 ? entry.pts : [...entry.pts, ...entry.pts])
            out.labels +=
                `<text class="plan-grouper-label" x="${fnum(c.x)}" y="${fnum(c.y - fs * 1.6)}" text-anchor="middle"` +
                ` font-size="${fnum(fs)}" font-weight="700" fill="${tints.get(id) ?? PLAN_COLORS.grouperLabel}" pointer-events="none">` +
                `${esc(entry.name)}</text>`
        }
    }
    return out
}

/** Parses a `view_box` string, tolerating commas and extra whitespace. */
export function parseViewBox(viewBox: string | undefined): { x: number; y: number; w: number; h: number } {
    const parts = String(viewBox ?? '').trim().split(/[\s,]+/).map(Number)
    if (parts.length !== 4 || parts.some((n) => !Number.isFinite(n)) || parts[2] <= 0 || parts[3] <= 0) {
        return { x: 0, y: 0, w: 100, h: 100 }
    }
    return { x: parts[0], y: parts[1], w: parts[2], h: parts[3] }
}

/**
 * Where an equipment's footprint actually sits, relative to its position.
 *
 * A `plan_symbol` is placed by its **anchor**, and an anchor is rarely the
 * middle of its viewBox — a basin anchors on its wall connection, a radiator on
 * its centre. So the drawn shape is offset from the element's position by
 * however far off-centre that anchor is, and everything that has to agree with
 * what the user can see — the hit area, the selection outline, the rotation arm
 * — has to apply the same offset. Drawing those around the position instead
 * leaves the box floating beside the symbol it claims to describe.
 *
 * The offset is expressed in the element's **unrotated** frame; callers rotate
 * it about the position along with everything else.
 */
export function equipmentFootprintOffset(
    symbol: PlanSymbol | undefined,
    width: number,
    depth: number,
): Point {
    if (!symbol) return { x: 0, y: 0 }
    const vb = parseViewBox(symbol.view_box)
    const ax = symbol.anchor?.x ?? vb.x + vb.w / 2
    const ay = symbol.anchor?.y ?? vb.y + vb.h / 2
    return {
        x: (vb.x + vb.w / 2 - ax) * (width / vb.w),
        y: (vb.y + vb.h / 2 - ay) * (depth / vb.h),
    }
}

/** The four world corners of an equipment's footprint, rotation included. */
export function equipmentFootprintCorners(
    el: { position: Point; rotationDeg: number; width: number; depth: number; typeCode?: string },
    symbol: PlanSymbol | undefined,
): Point[] {
    const off = equipmentFootprintOffset(symbol, el.width, el.depth)
    const rad = (el.rotationDeg * Math.PI) / 180
    const cos = Math.cos(rad)
    const sin = Math.sin(rad)
    return [[-1, -1], [1, -1], [1, 1], [-1, 1]].map(([sx, sy]) => {
        const lx = off.x + (sx * el.width) / 2
        const ly = off.y + (sy * el.depth) / 2
        return {
            x: el.position.x + lx * cos - ly * sin,
            y: el.position.y + lx * sin + ly * cos,
        }
    })
}

/**
 * Places an asset type's `plan_symbol`: the symbol's anchor lands exactly on
 * the element position and is the rotation pivot, and the footprint scales the
 * viewBox per axis (spec 2026-08-22-01 §5).
 */
export function symbolMarkup(
    symbol: PlanSymbol,
    position: Point,
    rotationDeg: number,
    width: number,
    depth: number,
): string {
    const vb = parseViewBox(symbol.view_box)
    const sx = width / vb.w
    const sy = depth / vb.h
    const ax = symbol.anchor?.x ?? vb.x + vb.w / 2
    const ay = symbol.anchor?.y ?? vb.y + vb.h / 2
    return (
        `<g transform="translate(${fnum(position.x)},${fnum(position.y)}) rotate(${fnum(rotationDeg)})` +
        ` scale(${sx},${sy}) translate(${fnum(-ax)},${fnum(-ay)})">${symbol.svg}</g>`
    )
}

/**
 * The smallest footprint a resize may leave — the same floor an opening's width
 * handle keeps, so a slip of the hand cannot shrink a symbol to nothing.
 */
export const EQUIPMENT_MIN_SIZE_MM = 50

/**
 * The footprint an `Alt`-drag to `pointer` gives an equipment.
 *
 * The pointer is un-rotated into the symbol's own frame and read as the far
 * edge of the footprint in each axis, so the shape follows the corner under the
 * hand — both axes at once, as a corner handle would — while the **anchor**,
 * the point the symbol is pinned and snapped by, stays exactly where it is.
 * That is why the reach below is not simply half the size: an off-centre anchor
 * (a bed pinned at its head) sits nearly a whole depth away from the far edge.
 */
export function equipmentSizeAt(
    el: { position: Point; rotationDeg: number },
    symbol: PlanSymbol | undefined,
    pointer: Point,
    upm: number = 1,
): { width: number; depth: number } {
    // The floor and the rounding are millimetre habits; `upm` (plan units per
    // millimetre) puts them in the frame's plan units (1 when the plan is in millimetres).
    const minSize = EQUIPMENT_MIN_SIZE_MM * upm
    const step = 10 * upm
    const rad = (el.rotationDeg * Math.PI) / 180
    const cos = Math.cos(rad)
    const sin = Math.sin(rad)
    const dx = pointer.x - el.position.x
    const dy = pointer.y - el.position.y
    const lx = dx * cos + dy * sin
    const ly = -dx * sin + dy * cos
    const vb = parseViewBox(symbol?.view_box)
    const kx = symbol ? (vb.x + vb.w / 2 - (symbol.anchor?.x ?? vb.x + vb.w / 2)) / vb.w : 0
    const ky = symbol ? (vb.y + vb.h / 2 - (symbol.anchor?.y ?? vb.y + vb.h / 2)) / vb.h : 0
    const reach = (k: number): number => Math.max(Math.abs(k + 0.5), Math.abs(k - 0.5))
    const size = (local: number, k: number): number =>
        Math.max(minSize, Math.round(Math.abs(local) / reach(k) / step) * step)
    return { width: size(lx, kx), depth: size(ly, ky) }
}

function fallbackEquipment(
    position: Point,
    rotationDeg: number,
    width: number,
    depth: number,
    typeCode: string | undefined,
    hairline: number,
): string {
    const glyph = (typeCode ?? 'EQ').split('_').map((s) => s.charAt(0).toUpperCase()).join('').slice(0, 3)
    // Both follow the footprint or the screen, never a fixed plan-unit floor:
    // a plan drawn on a 1-unit sheet would be buried under an 8-unit stroke.
    const fs = Math.min(width, depth) * 0.45
    return (
        `<g transform="rotate(${fnum(rotationDeg)} ${fnum(position.x)} ${fnum(position.y)})">` +
        `<rect x="${fnum(position.x - width / 2)}" y="${fnum(position.y - depth / 2)}" width="${fnum(width)}" height="${fnum(depth)}"` +
        ` fill="${PLAN_COLORS.equipmentFill}" stroke="${PLAN_COLORS.equipmentStroke}" stroke-width="${fnum(hairline)}"/>` +
        `<text x="${fnum(position.x)}" y="${fnum(position.y)}" text-anchor="middle" dominant-baseline="central"` +
        ` font-size="${fnum(fs)}" fill="${PLAN_COLORS.label}" pointer-events="none">${esc(glyph)}</text></g>`
    )
}
